import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Tenant } from "@/types";
import { Building2 } from "lucide-react";
import { useTenants } from "@/store/tenant";
import { useLayout } from "@/contexts/layout-context";
import { Button } from "@/components/ui/Button";
import { Loader } from "@/components/loader";
import {
  ComposedTable,
  AvatarCell,
  StatusCell,
  BadgeListCell,
  DateCell,
  ActionCell,
  type FilterDef,
  type FilterValues,
} from "@/components/table";
import { PageLayout } from "@/components/page-layout";


const FILTERS: FilterDef[] = [
  {
    type: "text",
    key: "domain",
    label: "Domain Name",
    placeholder: "e.g. argus.io",
  },
  {
    type: "chips",
    key: "idps",
    label: "Identity Providers",
    options: ["OKTA", "ENTRA ID"],
  },
  {
    type: "radio",
    key: "period",
    label: "Creation Period",
    options: ["All Time", "2024 Only", "2023 Only"],
  },
];

export const TenantsPage = () => {
  const { tenants, isLoading: loading, list, reorder } = useTenants();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useLayout();

  useEffect(() => {
    setBreadcrumbs([{ name: "Tenants" }]);
    list();
  }, [setBreadcrumbs, list]);

  const columns = [
    {
      id: "name",
      header: "Tenant Name",
      accessorKey: "name",
      cell: ({ row }: any) => {
        const t = row.original as Tenant;
        const idx = tenants.indexOf(t);
        return (
          <Link to={`/m/tenants/${t.id}`}>
            <AvatarCell
              name={t.name}
              subtitle={t.domain || t.slug}
              src={t.logo_url}
              index={idx}
            />
          </Link>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "id",
      cell: ({ row }: any) => {
        const statusMap: Record<number, string> = { 1: "Active", 2: "Inactive", 3: "Suspended" };
        const statusText = typeof row.original.status === 'number' ? (statusMap[row.original.status] || "Active") : (row.original.status || "Active");
        return <StatusCell status={statusText} />;
      },
    },
    {
      id: "active_users",
      header: "Active Users",
      cell: () => (
        <span className="text-[13px] font-medium text-slate-600 dark:text-slate-400">
          0
        </span>
      ),
    },
    {
      id: "idps",
      header: "Identity Providers",
      cell: () => (
        <BadgeListCell items={[]} />
      ),
    },
    {
      id: "created_at",
      header: "Created Date",
      accessorKey: "created_at",
      cell: ({ row }: any) => <DateCell value={row.original.created_at} />,
    },
    {
      id: "actions",
      header: "Actions",
      meta: { align: "right" as const },
      cell: ({ row }: any) => (
        <ActionCell
          onClick={() => navigate(`/m/tenants/${row.original.id}`)}
        />
      ),
    },
  ];

  const filterFn = (row: Tenant, values: FilterValues) => {
    const domain = values["domain"] as string;
    if (
      domain &&
      !(row.domain || row.slug || "")
        .toLowerCase()
        .includes(domain.toLowerCase())
    )
      return false;
    // IdP filter removed as we no longer have mock IdPs
    const period = values["period"] as string;
    if (period && period !== "All Time") {
      const year = new Date(row.created_at || Date.now()).getFullYear();
      if (period === "2024 Only" && year !== 2024) return false;
      if (period === "2023 Only" && year !== 2023) return false;
    }
    return true;
  };

  const handleReorder = (pageRows: Tenant[]) => {
    // Page rows keep the slots they occupied in the full list, in their new order.
    const moved = new Set(pageRows.map((t) => t.id));
    const queue = [...pageRows];
    reorder(tenants.map((t) => (moved.has(t.id) ? queue.shift()!.id : t.id)));
  };

  const emptyState = (
    <div className="p-12 text-center flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
      <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
        <Building2 className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
        No tenants found
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xs">
        Start by creating your first tenant to manage vendors and their
        configurations.
      </p>
      <Button
        className="mt-6 bg-[#1e293b] hover:bg-slate-800 text-white rounded-full px-6"
        onClick={() => navigate("/m/tenants/create")}
      >
        Create first tenant
      </Button>
    </div>
  );

  return (
    <PageLayout title="Tenants" description="Manage logical identity isolation and tenant configurations.">
      {loading ? (
        <div className="flex justify-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
          <Loader />
        </div>
      ) : (
        <ComposedTable
          data={tenants}
          columns={columns}
          searchPlaceholder="Search tenants by name, ID or domain..."
          statusOptions={["All", "Active", "Inactive"]}
          statusField={(t: Tenant) => {
            if (t.status === 1) return "Active";
            if (t.status === 2) return "Inactive";
            if (t.status === 3) return "Suspended";
            return "Active";
          }}
          filters={FILTERS}
          filterFn={filterFn}
          onAdd={() => navigate("/m/tenants/create")}
          emptyState={emptyState}
          itemLabel="tenants"
          onReorder={handleReorder}
          getRowKey={(t: Tenant) => t.id}
        />
      )}
    </PageLayout>
  );
};
